/**
 * GarmentIllustration.js
 * Layered SVG technical fashion illustrations for all garment types.
 * Each illustration responds to the customisation spec to update layers in real-time.
 */
import { useMemo } from 'react';

const STROKE = '#1B2A4A';
const STROKE_LIGHT = 'rgba(27,42,74,0.25)';
const FILL_BODY = 'rgba(27,42,74,0.03)';

/* ============================================================
   JACKET
   ============================================================ */
function JacketSVG({ spec = {}, fabricColor }) {
  const lapel = spec.lapel_style || 'notch';
  const buttons = spec.button_config || '2B';
  const pocket = spec.pocket_style || 'flap';
  const ticketPocket = spec.ticket_pocket === 'yes';
  const isDB = buttons.startsWith('DB');
  const fill = fabricColor || FILL_BODY;

  return (
    <g>
      {/* Body */}
      <path d="M120,60 L90,75 L75,340 L100,345 L105,200 L130,345 L170,345 L195,200 L200,345 L225,340 L210,75 L180,60 Z"
        fill={fill} stroke={STROKE} strokeWidth="1.5" strokeLinejoin="round" />
      {/* Shoulders */}
      <path d="M120,60 L65,80 L75,95 L90,75 Z" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      <path d="M180,60 L235,80 L225,95 L210,75 Z" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      {/* Center line */}
      <line x1="150" y1="80" x2="150" y2="340" stroke={STROKE_LIGHT} strokeWidth="0.5" strokeDasharray="4,3" />

      {/* Lapels */}
      {lapel === 'notch' && <>
        <path d="M150,80 L125,72 L110,100 L130,140 L150,130" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M150,80 L175,72 L190,100 L170,140 L150,130" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <line x1="125" y1="72" x2="115" y2="60" stroke={STROKE} strokeWidth="1.3" />
        <line x1="175" y1="72" x2="185" y2="60" stroke={STROKE} strokeWidth="1.3" />
      </>}
      {lapel === 'peak' && <>
        <path d="M150,80 L120,75 L110,100 L130,145 L150,130" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M150,80 L180,75 L190,100 L170,145 L150,130" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <line x1="120" y1="75" x2="100" y2="55" stroke={STROKE} strokeWidth="1.3" />
        <line x1="180" y1="75" x2="200" y2="55" stroke={STROKE} strokeWidth="1.3" />
      </>}
      {lapel === 'shawl' && <>
        <path d="M150,80 Q125,75 110,100 Q120,130 150,135" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M150,80 Q175,75 190,100 Q180,130 150,135" fill={fill} stroke={STROKE} strokeWidth="1.3" />
      </>}

      {/* Collar */}
      <path d="M120,60 L140,50 L150,55 L160,50 L180,60" fill={fill} stroke={STROKE} strokeWidth="1.3" fill="none" />

      {/* Buttons */}
      {!isDB && <>
        {(buttons === '1B' || buttons === '2B' || buttons === '3B') &&
          <circle cx="150" cy="175" r="3.5" fill="none" stroke={STROKE} strokeWidth="1" />}
        {(buttons === '2B' || buttons === '3B') &&
          <circle cx="150" cy="210" r="3.5" fill="none" stroke={STROKE} strokeWidth="1" />}
        {buttons === '3B' &&
          <circle cx="150" cy="145" r="3.5" fill="none" stroke={STROKE} strokeWidth="1" />}
      </>}
      {isDB && <>
        <circle cx="140" cy="165" r="3" fill="none" stroke={STROKE} strokeWidth="1" />
        <circle cx="140" cy="195" r="3" fill="none" stroke={STROKE} strokeWidth="1" />
        <circle cx="140" cy="225" r="3" fill="none" stroke={STROKE} strokeWidth="1" />
        <circle cx="160" cy="165" r="3" fill={STROKE} stroke={STROKE} strokeWidth="1" />
        <circle cx="160" cy="195" r="3" fill={STROKE} stroke={STROKE} strokeWidth="1" />
        {buttons === 'DB_6x2' && <circle cx="160" cy="225" r="3" fill={STROKE} stroke={STROKE} strokeWidth="1" />}
      </>}

      {/* Pockets */}
      {pocket === 'flap' && <>
        <rect x="95" y="225" width="38" height="4" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <rect x="95" y="229" width="38" height="22" rx="1" fill="none" stroke={STROKE_LIGHT} strokeWidth="0.6" strokeDasharray="3,2" />
        <rect x="167" y="225" width="38" height="4" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <rect x="167" y="229" width="38" height="22" rx="1" fill="none" stroke={STROKE_LIGHT} strokeWidth="0.6" strokeDasharray="3,2" />
      </>}
      {pocket === 'jetted' && <>
        <line x1="95" y1="228" x2="133" y2="228" stroke={STROKE} strokeWidth="1" />
        <line x1="95" y1="232" x2="133" y2="232" stroke={STROKE} strokeWidth="1" />
        <line x1="167" y1="228" x2="205" y2="228" stroke={STROKE} strokeWidth="1" />
        <line x1="167" y1="232" x2="205" y2="232" stroke={STROKE} strokeWidth="1" />
      </>}
      {pocket === 'patch' && <>
        <rect x="95" y="222" width="38" height="30" rx="3" fill="none" stroke={STROKE} strokeWidth="0.8" />
        <rect x="167" y="222" width="38" height="30" rx="3" fill="none" stroke={STROKE} strokeWidth="0.8" />
      </>}

      {/* Ticket pocket */}
      {ticketPocket && <rect x="95" y="200" width="25" height="3" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.7" />}

      {/* Breast pocket */}
      <line x1="160" y1="145" x2="183" y2="145" stroke={STROKE} strokeWidth="0.7" />
      <line x1="160" y1="145" x2="160" y2="165" stroke={STROKE_LIGHT} strokeWidth="0.5" strokeDasharray="2,2" />
    </g>
  );
}

/* ============================================================
   TROUSERS
   ============================================================ */
function TrousersSVG({ spec = {}, fabricColor }) {
  const pleat = spec.pleat || 'flat';
  const cuff = spec.cuff === 'yes';
  const pocket = spec.pocket || 'slant';
  const fill = fabricColor || FILL_BODY;

  return (
    <g>
      {/* Waistband */}
      <rect x="100" y="60" width="100" height="15" rx="2" fill={fill} stroke={STROKE} strokeWidth="1.3" />
      <line x1="150" y1="60" x2="150" y2="75" stroke={STROKE} strokeWidth="0.8" />

      {/* Left leg */}
      <path d="M100,75 L95,340 L145,340 L148,75 Z" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      {/* Right leg */}
      <path d="M152,75 L155,340 L205,340 L200,75 Z" fill={fill} stroke={STROKE} strokeWidth="1.2" />

      {/* Center crease */}
      <line x1="120" y1="90" x2="118" y2="335" stroke={STROKE_LIGHT} strokeWidth="0.5" strokeDasharray="6,4" />
      <line x1="178" y1="90" x2="180" y2="335" stroke={STROKE_LIGHT} strokeWidth="0.5" strokeDasharray="6,4" />

      {/* Pleats */}
      {pleat === 'single' && <>
        <line x1="125" y1="75" x2="124" y2="120" stroke={STROKE} strokeWidth="0.7" />
        <line x1="175" y1="75" x2="176" y2="120" stroke={STROKE} strokeWidth="0.7" />
      </>}
      {pleat === 'double' && <>
        <line x1="120" y1="75" x2="119" y2="115" stroke={STROKE} strokeWidth="0.7" />
        <line x1="130" y1="75" x2="129" y2="110" stroke={STROKE} strokeWidth="0.7" />
        <line x1="170" y1="75" x2="171" y2="115" stroke={STROKE} strokeWidth="0.7" />
        <line x1="180" y1="75" x2="181" y2="110" stroke={STROKE} strokeWidth="0.7" />
      </>}

      {/* Pockets */}
      {pocket === 'slant' && <>
        <line x1="100" y1="80" x2="125" y2="105" stroke={STROKE} strokeWidth="0.8" />
        <line x1="200" y1="80" x2="175" y2="105" stroke={STROKE} strokeWidth="0.8" />
      </>}
      {pocket === 'on_seam' && <>
        <line x1="100" y1="85" x2="100" y2="130" stroke={STROKE} strokeWidth="1" />
        <line x1="200" y1="85" x2="200" y2="130" stroke={STROKE} strokeWidth="1" />
      </>}
      {pocket === 'western' && <>
        <path d="M103,80 L103,120 Q110,122 120,100 Z" fill="none" stroke={STROKE} strokeWidth="0.8" />
        <path d="M197,80 L197,120 Q190,122 180,100 Z" fill="none" stroke={STROKE} strokeWidth="0.8" />
      </>}

      {/* Cuffs */}
      {cuff && <>
        <rect x="95" y="330" width="50" height="10" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <rect x="155" y="330" width="50" height="10" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.8" />
      </>}

      {/* Belt loops */}
      <rect x="110" y="57" width="4" height="18" rx="1" fill="none" stroke={STROKE} strokeWidth="0.6" />
      <rect x="130" y="57" width="4" height="18" rx="1" fill="none" stroke={STROKE} strokeWidth="0.6" />
      <rect x="166" y="57" width="4" height="18" rx="1" fill="none" stroke={STROKE} strokeWidth="0.6" />
      <rect x="186" y="57" width="4" height="18" rx="1" fill="none" stroke={STROKE} strokeWidth="0.6" />
    </g>
  );
}

/* ============================================================
   SHIRT
   ============================================================ */
function ShirtSVG({ spec = {}, fabricColor }) {
  const collar = spec.collar || 'spread';
  const cuff = spec.cuff || 'single';
  const pocket = spec.pocket || 'none';
  const placket = spec.placket || 'standard';
  const fill = fabricColor || FILL_BODY;

  return (
    <g>
      {/* Body */}
      <path d="M120,75 L85,340 L110,345 L115,200 L150,345 L185,200 L185,345 L215,340 L180,75 Z"
        fill={fill} stroke={STROKE} strokeWidth="1.2" strokeLinejoin="round" />
      {/* Shoulders + sleeves */}
      <path d="M120,75 L60,90 L55,100 L65,170 L80,165 L90,105 L120,80" fill={fill} stroke={STROKE} strokeWidth="1" />
      <path d="M180,75 L240,90 L245,100 L235,170 L220,165 L210,105 L180,80" fill={fill} stroke={STROKE} strokeWidth="1" />

      {/* Placket */}
      {placket === 'standard' && <>
        <line x1="148" y1="75" x2="148" y2="340" stroke={STROKE} strokeWidth="0.6" />
        <line x1="152" y1="75" x2="152" y2="340" stroke={STROKE} strokeWidth="0.6" />
      </>}
      {placket === 'hidden' &&
        <line x1="150" y1="75" x2="150" y2="340" stroke={STROKE_LIGHT} strokeWidth="0.4" strokeDasharray="4,3" />
      }
      {placket === 'french_front' && <>
        <line x1="146" y1="75" x2="146" y2="340" stroke={STROKE} strokeWidth="0.6" />
        <line x1="154" y1="75" x2="154" y2="340" stroke={STROKE} strokeWidth="0.6" />
        <line x1="146" y1="75" x2="146" y2="340" stroke={STROKE} strokeWidth="1.2" />
      </>}

      {/* Buttons */}
      {[95, 125, 160, 200, 240, 280, 320].map(y => (
        <circle key={y} cx="150" cy={y} r="2.5" fill="none" stroke={STROKE} strokeWidth="0.7" />
      ))}

      {/* Collar */}
      {collar === 'spread' && <>
        <path d="M120,75 L140,62 L150,68 L160,62 L180,75" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M140,62 L110,52 L115,58 L140,65" fill={fill} stroke={STROKE} strokeWidth="1" />
        <path d="M160,62 L190,52 L185,58 L160,65" fill={fill} stroke={STROKE} strokeWidth="1" />
      </>}
      {collar === 'cutaway' && <>
        <path d="M120,75 L140,62 L150,68 L160,62 L180,75" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M140,62 L100,48 L108,56 L140,66" fill={fill} stroke={STROKE} strokeWidth="1" />
        <path d="M160,62 L200,48 L192,56 L160,66" fill={fill} stroke={STROKE} strokeWidth="1" />
      </>}
      {collar === 'button_down' && <>
        <path d="M120,75 L140,62 L150,68 L160,62 L180,75" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M140,62 L118,55 L122,60 L140,66" fill={fill} stroke={STROKE} strokeWidth="1" />
        <path d="M160,62 L182,55 L178,60 L160,66" fill={fill} stroke={STROKE} strokeWidth="1" />
        <circle cx="130" cy="82" r="1.5" fill={STROKE} stroke={STROKE} strokeWidth="0.5" />
        <circle cx="170" cy="82" r="1.5" fill={STROKE} stroke={STROKE} strokeWidth="0.5" />
      </>}
      {(collar === 'mandarin' || collar === 'band') && <>
        <rect x="125" y="55" width="50" height="20" rx="3" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      </>}
      {collar === 'club' && <>
        <path d="M120,75 L140,62 L150,68 L160,62 L180,75" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M140,62 Q115,50 120,60 L140,66" fill={fill} stroke={STROKE} strokeWidth="1" />
        <path d="M160,62 Q185,50 180,60 L160,66" fill={fill} stroke={STROKE} strokeWidth="1" />
      </>}
      {collar === 'tab' && <>
        <path d="M120,75 L140,62 L150,68 L160,62 L180,75" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M140,62 L120,56 L125,60 L140,66" fill={fill} stroke={STROKE} strokeWidth="1" />
        <path d="M160,62 L180,56 L175,60 L160,66" fill={fill} stroke={STROKE} strokeWidth="1" />
        <rect x="142" y="70" width="16" height="5" rx="1.5" fill="none" stroke={STROKE} strokeWidth="0.6" />
      </>}
      {collar === 'wing_tip' && <>
        <path d="M120,75 L140,62 L150,66 L160,62 L180,75" fill={fill} stroke={STROKE} strokeWidth="1.3" />
        <path d="M140,62 L128,52 L130,60 L140,64" fill={fill} stroke={STROKE} strokeWidth="1" />
        <path d="M160,62 L172,52 L170,60 L160,64" fill={fill} stroke={STROKE} strokeWidth="1" />
      </>}

      {/* Cuffs */}
      {cuff === 'single' && <>
        <rect x="55" y="162" width="28" height="8" rx="2" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <rect x="218" y="162" width="28" height="8" rx="2" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <circle cx="63" cy="166" r="1.5" fill="none" stroke={STROKE} strokeWidth="0.5" />
        <circle cx="238" cy="166" r="1.5" fill="none" stroke={STROKE} strokeWidth="0.5" />
      </>}
      {(cuff === 'double' || cuff === 'french') && <>
        <rect x="52" y="158" width="32" height="14" rx="2" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <line x1="52" y1="165" x2="84" y2="165" stroke={STROKE_LIGHT} strokeWidth="0.5" />
        <rect x="216" y="158" width="32" height="14" rx="2" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <line x1="216" y1="165" x2="248" y2="165" stroke={STROKE_LIGHT} strokeWidth="0.5" />
      </>}

      {/* Pocket */}
      {pocket === 'single' && <rect x="160" y="115" width="25" height="28" rx="2" fill="none" stroke={STROKE} strokeWidth="0.7" />}
      {pocket === 'double' && <>
        <rect x="113" y="115" width="25" height="28" rx="2" fill="none" stroke={STROKE} strokeWidth="0.7" />
        <rect x="162" y="115" width="25" height="28" rx="2" fill="none" stroke={STROKE} strokeWidth="0.7" />
      </>}
    </g>
  );
}

/* ============================================================
   SHERWANI
   ============================================================ */
function SherwaniSVG({ spec = {}, fabricColor }) {
  const collar = spec.collar || 'mandarin';
  const length = spec.length || 'knee';
  const buttonCount = parseInt(spec.button_count) || 7;
  const embroidery = spec.embroidery || 'none';
  const fill = fabricColor || FILL_BODY;
  const bottomY = length === 'ankle' ? 360 : length === 'below_knee' ? 320 : 290;

  return (
    <g>
      {/* Body */}
      <path d={`M120,65 L90,80 L80,${bottomY} L150,${bottomY + 8} L220,${bottomY} L210,80 L180,65 Z`}
        fill={fill} stroke={STROKE} strokeWidth="1.3" strokeLinejoin="round" />
      {/* Sleeves */}
      <path d="M120,65 L55,85 L52,95 L60,185 L78,180 L85,100" fill={fill} stroke={STROKE} strokeWidth="1" />
      <path d="M180,65 L245,85 L248,95 L240,185 L222,180 L215,100" fill={fill} stroke={STROKE} strokeWidth="1" />

      {/* Collar */}
      {collar === 'mandarin' && <rect x="125" y="50" width="50" height="18" rx="3" fill={fill} stroke={STROKE} strokeWidth="1.2" />}
      {collar === 'shawl' && <>
        <path d="M150,68 Q125,60 115,80 Q120,110 150,115" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <path d="M150,68 Q175,60 185,80 Q180,110 150,115" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      </>}
      {collar === 'notch' && <>
        <path d="M150,68 L125,62 L115,90 L135,125 L150,118" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <path d="M150,68 L175,62 L185,90 L165,125 L150,118" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <line x1="125" y1="62" x2="118" y2="50" stroke={STROKE} strokeWidth="1.2" />
        <line x1="175" y1="62" x2="182" y2="50" stroke={STROKE} strokeWidth="1.2" />
      </>}
      {collar === 'band' && <rect x="125" y="55" width="50" height="12" rx="2" fill={fill} stroke={STROKE} strokeWidth="1" />}

      {/* Center placket */}
      <line x1="150" y1="68" x2="150" y2={bottomY} stroke={STROKE} strokeWidth="0.6" />

      {/* Buttons */}
      {Array.from({ length: Math.min(buttonCount, 9) }, (_, i) => {
        const totalSpan = bottomY - 90;
        const y = 90 + (totalSpan * i) / (Math.max(buttonCount - 1, 1));
        return <circle key={i} cx="150" cy={y} r="2.5" fill="none" stroke={STROKE} strokeWidth="0.7" />;
      })}

      {/* Embroidery indicator */}
      {embroidery !== 'none' && <>
        {(embroidery === 'collar' || embroidery === 'collar_cuffs' || embroidery === 'front' || embroidery === 'all_over') &&
          <path d="M130,55 Q135,48 140,55 Q145,48 150,55 Q155,48 160,55 Q165,48 170,55" fill="none" stroke="rgba(197,165,90,0.6)" strokeWidth="0.8" />
        }
        {(embroidery === 'front' || embroidery === 'all_over') && <>
          <path d={`M140,120 Q145,115 150,120 Q155,115 160,120`} fill="none" stroke="rgba(197,165,90,0.4)" strokeWidth="0.6" />
          <path d={`M135,180 Q140,175 145,180 Q150,175 155,180 Q160,175 165,180`} fill="none" stroke="rgba(197,165,90,0.4)" strokeWidth="0.6" />
        </>}
      </>}
    </g>
  );
}

/* ============================================================
   BANDHGALA
   ============================================================ */
function BandhgalaSVG({ spec = {}, fabricColor }) {
  const buttonCount = parseInt(spec.button_count) || 5;
  const pocket = spec.pocket || 'jetted';
  const embroidery = spec.embroidery || 'none';
  const fill = fabricColor || FILL_BODY;

  return (
    <g>
      {/* Body */}
      <path d="M120,65 L90,80 L80,310 L220,310 L210,80 L180,65 Z"
        fill={fill} stroke={STROKE} strokeWidth="1.3" strokeLinejoin="round" />
      {/* Sleeves */}
      <path d="M120,65 L58,85 L55,95 L62,180 L78,175 L85,100" fill={fill} stroke={STROKE} strokeWidth="1" />
      <path d="M180,65 L242,85 L245,95 L238,180 L222,175 L215,100" fill={fill} stroke={STROKE} strokeWidth="1" />
      {/* Mandarin collar */}
      <rect x="125" y="50" width="50" height="18" rx="3" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      {/* Center line */}
      <line x1="150" y1="68" x2="150" y2="310" stroke={STROKE} strokeWidth="0.6" />
      {/* Buttons */}
      {Array.from({ length: Math.min(buttonCount, 7) }, (_, i) => (
        <circle key={i} cx="150" cy={90 + i * 35} r="2.5" fill="none" stroke={STROKE} strokeWidth="0.7" />
      ))}
      {/* Pockets */}
      {pocket === 'jetted' && <>
        <line x1="95" y1="200" x2="135" y2="200" stroke={STROKE} strokeWidth="0.8" />
        <line x1="95" y1="204" x2="135" y2="204" stroke={STROKE} strokeWidth="0.8" />
        <line x1="165" y1="200" x2="205" y2="200" stroke={STROKE} strokeWidth="0.8" />
        <line x1="165" y1="204" x2="205" y2="204" stroke={STROKE} strokeWidth="0.8" />
      </>}
      {pocket === 'flap' && <>
        <rect x="95" y="198" width="40" height="4" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.8" />
        <rect x="165" y="198" width="40" height="4" rx="1" fill={fill} stroke={STROKE} strokeWidth="0.8" />
      </>}
      {pocket === 'welt' && <>
        <line x1="95" y1="202" x2="135" y2="202" stroke={STROKE} strokeWidth="1" />
        <line x1="165" y1="202" x2="205" y2="202" stroke={STROKE} strokeWidth="1" />
      </>}
      {/* Embroidery */}
      {embroidery !== 'none' &&
        <path d="M130,52 Q135,46 140,52 Q145,46 150,52 Q155,46 160,52 Q165,46 170,52" fill="none" stroke="rgba(197,165,90,0.6)" strokeWidth="0.8" />
      }
    </g>
  );
}

/* ============================================================
   NEHRU VEST
   ============================================================ */
function NehruVestSVG({ spec = {}, fabricColor }) {
  const buttonCount = parseInt(spec.button_count) || 5;
  const fill = fabricColor || FILL_BODY;

  return (
    <g>
      {/* Body — no sleeves */}
      <path d="M115,65 L90,80 L85,280 L215,280 L210,80 L185,65 Z"
        fill={fill} stroke={STROKE} strokeWidth="1.3" strokeLinejoin="round" />
      {/* Armholes */}
      <path d="M115,65 L100,80 L95,140 L115,135" fill="none" stroke={STROKE} strokeWidth="1" />
      <path d="M185,65 L200,80 L205,140 L185,135" fill="none" stroke={STROKE} strokeWidth="1" />
      {/* Mandarin collar */}
      <rect x="125" y="52" width="50" height="16" rx="3" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      {/* Center */}
      <line x1="150" y1="68" x2="150" y2="280" stroke={STROKE} strokeWidth="0.6" />
      {/* Buttons */}
      {Array.from({ length: buttonCount }, (_, i) => (
        <circle key={i} cx="150" cy={85 + i * 35} r="2.5" fill="none" stroke={STROKE} strokeWidth="0.7" />
      ))}
    </g>
  );
}

/* ============================================================
   KURTA
   ============================================================ */
function KurtaSVG({ spec = {}, fabricColor }) {
  const collar = spec.collar || 'mandarin';
  const length = spec.length || 'knee';
  const fill = fabricColor || FILL_BODY;
  const bottomY = length === 'below_knee' ? 340 : length === 'above_knee' ? 270 : 300;

  return (
    <g>
      {/* Body */}
      <path d={`M120,65 L85,80 L75,${bottomY} L225,${bottomY} L215,80 L180,65 Z`}
        fill={fill} stroke={STROKE} strokeWidth="1.3" strokeLinejoin="round" />
      {/* Sleeves */}
      <path d="M120,65 L55,85 L52,95 L60,190 L78,185 L85,100" fill={fill} stroke={STROKE} strokeWidth="1" />
      <path d="M180,65 L245,85 L248,95 L240,190 L222,185 L215,100" fill={fill} stroke={STROKE} strokeWidth="1" />

      {/* Collar */}
      {collar === 'mandarin' && <rect x="125" y="50" width="50" height="18" rx="3" fill={fill} stroke={STROKE} strokeWidth="1.2" />}
      {collar === 'round' && <path d="M120,65 Q150,50 180,65" fill="none" stroke={STROKE} strokeWidth="1.3" />}
      {collar === 'v_neck' && <path d="M130,65 L150,95 L170,65" fill="none" stroke={STROKE} strokeWidth="1.3" />}
      {collar === 'band' && <rect x="128" y="55" width="44" height="12" rx="2" fill={fill} stroke={STROKE} strokeWidth="1" />}
      {collar === 'open' && <path d="M135,65 L140,90 L150,100 L160,90 L165,65" fill="none" stroke={STROKE} strokeWidth="1.2" />}

      {/* Center placket */}
      <line x1="150" y1="68" x2="150" y2={Math.min(bottomY - 40, 180)} stroke={STROKE} strokeWidth="0.6" />
      {[90, 115, 140].map(y => <circle key={y} cx="150" cy={y} r="2" fill="none" stroke={STROKE} strokeWidth="0.6" />)}

      {/* Side slit */}
      <line x1="85" y1={bottomY - 50} x2="85" y2={bottomY} stroke={STROKE_LIGHT} strokeWidth="0.6" />
      <line x1="215" y1={bottomY - 50} x2="215" y2={bottomY} stroke={STROKE_LIGHT} strokeWidth="0.6" />
    </g>
  );
}

/* ============================================================
   WAISTCOAT
   ============================================================ */
function WaistcoatSVG({ spec = {}, fabricColor }) {
  const lapel = spec.lapel || 'none';
  const buttonCount = parseInt(spec.button_count) || 5;
  const fill = fabricColor || FILL_BODY;

  return (
    <g>
      {/* Body — no sleeves */}
      <path d="M115,65 L90,80 L95,290 L130,295 L150,340 L170,295 L205,290 L210,80 L185,65 Z"
        fill={fill} stroke={STROKE} strokeWidth="1.3" strokeLinejoin="round" />
      {/* Armholes */}
      <path d="M115,65 L100,80 L95,145 L115,140" fill="none" stroke={STROKE} strokeWidth="1" />
      <path d="M185,65 L200,80 L205,145 L185,140" fill="none" stroke={STROKE} strokeWidth="1" />

      {/* Collar/Lapel */}
      {lapel === 'none' &&
        <path d="M115,65 L140,55 L150,60 L160,55 L185,65" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      }
      {lapel === 'notch' && <>
        <path d="M150,68 L125,62 L115,88 L135,120 L150,112" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <path d="M150,68 L175,62 L185,88 L165,120 L150,112" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <line x1="125" y1="62" x2="118" y2="52" stroke={STROKE} strokeWidth="1.2" />
        <line x1="175" y1="62" x2="182" y2="52" stroke={STROKE} strokeWidth="1.2" />
      </>}
      {lapel === 'shawl' && <>
        <path d="M150,68 Q125,62 115,85 Q120,110 150,118" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <path d="M150,68 Q175,62 185,85 Q180,110 150,118" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      </>}
      {lapel === 'peak' && <>
        <path d="M150,68 L120,62 L112,90 L135,125 L150,115" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <path d="M150,68 L180,62 L188,90 L165,125 L150,115" fill={fill} stroke={STROKE} strokeWidth="1.2" />
        <line x1="120" y1="62" x2="102" y2="48" stroke={STROKE} strokeWidth="1.2" />
        <line x1="180" y1="62" x2="198" y2="48" stroke={STROKE} strokeWidth="1.2" />
      </>}

      {/* Center */}
      <line x1="150" y1="68" x2="150" y2="290" stroke={STROKE} strokeWidth="0.6" />
      {/* Buttons */}
      {Array.from({ length: buttonCount }, (_, i) => {
        const y = 100 + (i * (190 / Math.max(buttonCount - 1, 1)));
        return <circle key={i} cx="150" cy={y} r="2.5" fill="none" stroke={STROKE} strokeWidth="0.7" />;
      })}
    </g>
  );
}

/* ============================================================
   PYJAMA
   ============================================================ */
function PyjamaSVG({ spec = {}, fabricColor }) {
  const style = spec.style || 'straight';
  const cuff = spec.cuff || 'tapered';
  const fill = fabricColor || FILL_BODY;

  const leftOuter = style === 'churidar' ? 110 : style === 'salwar' ? 80 : 95;
  const leftInner = style === 'churidar' ? 140 : 145;
  const rightOuter = style === 'churidar' ? 190 : style === 'salwar' ? 220 : 205;
  const rightInner = style === 'churidar' ? 160 : 155;
  const cuffWidth = cuff === 'tapered' ? 8 : 0;

  return (
    <g>
      {/* Waistband */}
      <path d="M90,60 L90,78 L210,78 L210,60 Z" fill={fill} stroke={STROKE} strokeWidth="1.2" />
      <line x1="150" y1="60" x2="150" y2="78" stroke={STROKE} strokeWidth="0.6" />

      {/* Left leg */}
      {style === 'churidar' ? (
        <path d={`M95,78 Q80,200 ${leftOuter + cuffWidth},340 L${leftInner - cuffWidth},340 Q148,200 148,78 Z`}
          fill={fill} stroke={STROKE} strokeWidth="1.1" />
      ) : style === 'salwar' ? (
        <path d={`M95,78 Q${leftOuter},180 ${leftOuter + 15},340 L${leftInner - 5},340 Q148,200 148,78 Z`}
          fill={fill} stroke={STROKE} strokeWidth="1.1" />
      ) : (
        <path d="M95,78 L95,340 L145,340 L148,78 Z" fill={fill} stroke={STROKE} strokeWidth="1.1" />
      )}
      {/* Right leg */}
      {style === 'churidar' ? (
        <path d={`M152,78 Q152,200 ${rightInner + cuffWidth},340 L${rightOuter - cuffWidth},340 Q220,200 205,78 Z`}
          fill={fill} stroke={STROKE} strokeWidth="1.1" />
      ) : style === 'salwar' ? (
        <path d={`M152,78 Q152,200 ${rightInner + 5},340 L${rightOuter - 15},340 Q${rightOuter},180 205,78 Z`}
          fill={fill} stroke={STROKE} strokeWidth="1.1" />
      ) : (
        <path d="M152,78 L155,340 L205,340 L205,78 Z" fill={fill} stroke={STROKE} strokeWidth="1.1" />
      )}

      {/* Drawstring */}
      <path d="M135,67 Q140,73 145,67 Q148,63 150,67 Q152,63 155,67 Q160,73 165,67" fill="none" stroke={STROKE} strokeWidth="0.6" />
    </g>
  );
}

/* ============================================================
   Main Export
   ============================================================ */
export default function GarmentIllustration({ garmentType, spec = {}, fabricColor, className }) {
  const SvgComponent = useMemo(() => {
    switch (garmentType) {
      case 'jacket': return JacketSVG;
      case 'trousers': return TrousersSVG;
      case 'shirt': return ShirtSVG;
      case 'sherwani': return SherwaniSVG;
      case 'bandhgala': return BandhgalaSVG;
      case 'nehru_vest': return NehruVestSVG;
      case 'kurta': return KurtaSVG;
      case 'waistcoat': return WaistcoatSVG;
      case 'pyjama': return PyjamaSVG;
      default: return JacketSVG;
    }
  }, [garmentType]);

  return (
    <svg viewBox="0 0 300 400" className={className} xmlns="http://www.w3.org/2000/svg"
      data-testid={`garment-illustration-${garmentType}`}>
      <SvgComponent spec={spec} fabricColor={fabricColor} />
    </svg>
  );
}
